import { useEffect, useState } from "react";
import {
    ArrowLeft, Search, Eye, FileText,
    Hash, Layers, Flag, Circle, Loader2, Users, Calendar
} from "lucide-react";
import Swal from "sweetalert2";
import config from "../../config";
import TicketModal from "./TicketModal";

export default function TicketPending({ handlePageChange, currentUser }) {
    const [tickets, setTickets] = useState([]);
    const [search, setSearch] = useState("");
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(true);

    const API_TICKETS = `${config.API_BASE_URL}/Tickets`;

    const loadTickets = async () => {
        try {
            setLoading(true);
            const res = await fetch(API_TICKETS);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();

            // Same logic as TaskTicket — check localStorage + currentUser
            const adminData = JSON.parse(localStorage.getItem("admin") || "{}");
            const userData = JSON.parse(localStorage.getItem("user") || "{}");

            const isSuperAdmin = adminData?.role === "superadmin";

            const currentFullName =
                currentUser?.fullNames ||
                adminData?.fullNames ||
                userData?.fullNames ||
                null;

            // Step 1: Only Pending or In Progress
            let filtered = data.filter(
                (t) => t.status === "Pending" || t.status === "In Progress"
            );

            // Step 2: Superadmin sees all, others filtered by name
            if (!isSuperAdmin && currentFullName) {
                filtered = filtered.filter((ticket) => {
                    if (!ticket.assignees) return false;
                    return ticket.assignees
                        .toLowerCase()
                        .includes(currentFullName.toLowerCase());
                });
            } else if (!isSuperAdmin && !currentFullName) {
                filtered = [];
            }

            setTickets(filtered);
        } catch (error) {
            console.error("loadTickets error:", error);
            Swal.fire("Error", "Failed to load tickets", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadTickets(); }, [currentUser]);

    const isAdmin =
        JSON.parse(localStorage.getItem("admin") || "{}")?.role === "superadmin" ||
        currentUser?.role === "Admin" ||
        currentUser?.isAdmin;

    const displayed = tickets.filter(
        (ticket) =>
            ticket.ticketNumber?.toLowerCase().includes(search.toLowerCase()) ||
            ticket.subject?.toLowerCase().includes(search.toLowerCase()) ||
            ticket.category?.toLowerCase().includes(search.toLowerCase()) ||
            ticket.assignees?.toLowerCase().includes(search.toLowerCase())
    );

    const pendingCount = tickets.filter((t) => t.status === "Pending").length;
    const inProgressCount = tickets.filter((t) => t.status === "In Progress").length;

    const formatDate = (dateString) => {
        if (!dateString) return "—";
        return new Date(dateString).toLocaleDateString("en-PH", {
            month: "short", day: "numeric", year: "numeric",
        });
    };

    const getPriorityConfig = (priority) => {
        const map = {
            Low:      { color: "text-emerald-400", bg: "bg-emerald-500/15 border-emerald-500/30", dot: "bg-emerald-400" },
            Medium:   { color: "text-amber-400",   bg: "bg-amber-500/15 border-amber-500/30",     dot: "bg-amber-400" },
            High:     { color: "text-orange-400",  bg: "bg-orange-500/15 border-orange-500/30",   dot: "bg-orange-400" },
            Critical: { color: "text-red-400",     bg: "bg-red-500/15 border-red-500/30",         dot: "bg-red-400" },
        };
        return map[priority] || { color: "text-slate-400", bg: "bg-slate-700/50 border-slate-600/30", dot: "bg-slate-400" };
    };

    const getStatusConfig = (status) => {
        const map = {
            "Pending":     { color: "text-yellow-400", bg: "bg-yellow-500/10 border-yellow-500/30", icon: <Circle size={12} /> },
            "In Progress": { color: "text-blue-400",   bg: "bg-blue-500/10 border-blue-500/30",     icon: <Loader2 size={12} className="animate-spin" /> },
        };
        return map[status] || { color: "text-slate-400", bg: "bg-slate-700 border-slate-600", icon: <Circle size={12} /> };
    };

    const gridCols = isAdmin
        ? "grid-cols-[1.5fr_3fr_1.5fr_1.2fr_1.4fr_1.5fr_1.2fr_90px]"
        : "grid-cols-[1.5fr_3fr_1.5fr_1.2fr_1.4fr_1.2fr_90px]";

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100">
            <div className="border-b border-slate-800/60 bg-slate-900/50 backdrop-blur-sm px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => handlePageChange("dashboard")}
                            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700/60 transition-all"
                        >
                            <ArrowLeft size={18} />
                        </button>
                        <div>
                            <h1 className="text-xl font-bold tracking-tight text-white">Pending Tickets</h1>
                            <p className="text-xs text-slate-400 mt-0.5">
                                {isAdmin
                                    ? "All pending & in-progress tickets · Admin"
                                    : `Assigned to ${currentUser?.fullNames || "you"}`}
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <div className="px-3 py-1.5 rounded-lg border bg-yellow-500/10 border-yellow-500/30 text-yellow-400 text-xs font-medium flex items-center gap-2">
                            <span className="text-slate-400">Pending</span>
                            <span className="font-bold text-sm">{pendingCount}</span>
                        </div>
                        <div className="px-3 py-1.5 rounded-lg border bg-blue-500/10 border-blue-500/30 text-blue-400 text-xs font-medium flex items-center gap-2">
                            <span className="text-slate-400">In Progress</span>
                            <span className="font-bold text-sm">{inProgressCount}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="w-full px-6 py-6">
                <div className="flex justify-end mb-5">
                    <div className="relative">
                        <Search className="absolute left-3 top-2.5 text-slate-500" size={16} />
                        <input
                            className="pl-9 pr-4 py-2 bg-slate-800/60 border border-slate-700/50 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/50 transition-all w-64 placeholder:text-slate-500"
                            placeholder="Search tickets..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                <div className="rounded-2xl border border-slate-800/60 overflow-hidden bg-slate-900/40 backdrop-blur-sm">
                    <div className={`grid ${gridCols} gap-4 px-5 py-3 bg-slate-800/50 border-b border-slate-800/60 text-xs font-semibold text-slate-500 uppercase tracking-wider`}>
                        <div className="flex items-center gap-1.5"><Hash size={11} /> Ticket #</div>
                        <div className="flex items-center gap-1.5"><FileText size={11} /> Subject</div>
                        <div className="flex items-center gap-1.5"><Layers size={11} /> Category</div>
                        <div className="flex items-center gap-1.5"><Flag size={11} /> Priority</div>
                        <div className="flex items-center gap-1.5"><Circle size={11} /> Status</div>
                        {isAdmin && <div className="flex items-center gap-1.5"><Users size={11} /> Assignee</div>}
                        <div className="flex items-center gap-1.5"><Calendar size={11} /> Date</div>
                        <div />
                    </div>

                    {loading ? (
                        <div className="py-20 text-center text-slate-500 text-sm">Loading tickets...</div>
                    ) : displayed.length === 0 ? (
                        <div className="py-20 text-center">
                            <FileText size={48} className="mx-auto mb-3 text-slate-700" />
                            <p className="text-slate-500 text-sm">No pending tickets found</p>
                        </div>
                    ) : (
                        displayed.map((ticket, idx) => {
                            const priority = getPriorityConfig(ticket.priority);
                            const status = getStatusConfig(ticket.status);
                            return (
                                <div
                                    key={ticket.ticketNumber}
                                    className={`grid ${gridCols} gap-4 px-5 py-3.5 items-center text-sm border-b border-slate-800/40 hover:bg-slate-800/30 transition-colors group ${idx % 2 !== 0 ? "bg-slate-900/20" : ""}`}
                                >
                                    <div className="font-mono text-blue-400 font-medium text-xs">{ticket.ticketNumber}</div>
                                    <div className="text-slate-200 font-medium truncate pr-2" title={ticket.subject}>{ticket.subject}</div>
                                    <div className="text-slate-400 text-xs truncate">{ticket.category || "—"}</div>
                                    <div>
                                        {ticket.priority ? (
                                            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border ${priority.bg} ${priority.color}`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${priority.dot}`} />
                                                {ticket.priority}
                                            </span>
                                        ) : "—"}
                                    </div>
                                    <div>
                                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border ${status.bg} ${status.color}`}>
                                            {status.icon}
                                            {ticket.status}
                                        </span>
                                    </div>
                                    {isAdmin && (
                                        <div className="text-slate-400 text-xs truncate">{ticket.assignees || "—"}</div>
                                    )}
                                    <div className="text-slate-500 text-xs whitespace-nowrap">{formatDate(ticket.createdAt)}</div>
                                    <div className="flex justify-end">
                                        <button
                                            onClick={() => { setSelectedTicket(ticket); setShowModal(true); }}
                                            className="opacity-0 group-hover:opacity-100 flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/30 text-blue-400 text-xs font-medium transition-all"
                                        >
                                            <Eye size={12} /> View
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {displayed.length > 0 && (
                    <div className="mt-3 text-xs text-slate-500 text-right">
                        Showing {displayed.length} of {tickets.length} tickets
                    </div>
                )}
            </div>

            {showModal && selectedTicket && (
                <TicketModal
                    ticket={selectedTicket}
                    onClose={() => { setShowModal(false); setSelectedTicket(null); }}
                    formatDate={formatDate}
                    getPriorityColor={(p) => getPriorityConfig(p).dot}
                />
            )}
        </div>
    );
}